import type { ChartableColumn } from './chartableColumns';
import {
  BRAND_MUTED,
  BRAND_NAVY,
  BRAND_PINK,
  getCategoryCounts,
  getMetricDistribution,
  getYearBucketCounts,
} from './analyticsHelpers';
import type { ColumnChartType } from './columnChartTypes';
import { getDefaultColumnChartType, resolveColumnChartType } from './columnChartTypes';
import type { MetricDomain } from './metricCatalog';
import type { AssociationRow } from './analyticsTypes';

export interface ColumnChartItem {
  label: string;
  value: number;
  isOther: boolean;
  color: string;
}

export interface ColumnChartModel {
  items: ColumnChartItem[];
  chartType: ColumnChartType;
  orientation: 'horizontal' | 'vertical';
  highlightLabel: string | null;
  total: number;
}

const MAX_CATEGORY_BARS = 9;

const BAR_COLOR = BRAND_NAVY;
const BAR_COLOR_HIGHLIGHT = BRAND_PINK;
const BAR_COLOR_OTHER = BRAND_MUTED;

function colorForItem(label: string, isOther: boolean, highlightLabel: string | null): string {
  if (isOther) return BAR_COLOR_OTHER;
  return label === highlightLabel ? BAR_COLOR_HIGHLIGHT : BAR_COLOR;
}

export function buildColumnChartModel(
  column: ChartableColumn,
  data: AssociationRow[],
  options: {
    domain?: MetricDomain;
    chartType?: ColumnChartType | null;
  } = {},
): ColumnChartModel {
  const chartType = resolveColumnChartType(options.chartType, column.kind);
  const domain = options.domain ?? { kind: 'auto' };

  let raw: Array<{ label: string; value: number; isOther: boolean }>;

  if (column.kind === 'numeric') {
    raw = getMetricDistribution(data, column.field, domain).map(slice => ({
      label: slice.id,
      value: slice.value,
      isOther: false,
    }));
  } else if (column.kind === 'year') {
    raw = getYearBucketCounts(data, column.field).map(bucket => ({
      label: bucket.label,
      value: bucket.value,
      isOther: false,
    }));
  } else {
    raw = getCategoryCounts(data, column.field, { limit: MAX_CATEGORY_BARS }).map(entry => ({
      label: entry.label,
      value: entry.value,
      isOther: entry.id === '__other__',
    }));
  }

  const ranked = raw.filter(item => !item.isOther && item.value > 0);
  const top = ranked.reduce<(typeof raw)[number] | null>(
    (best, item) => (!best || item.value > best.value ? item : best),
    null,
  );
  const highlightLabel = top?.label ?? null;

  const items: ColumnChartItem[] = raw.map(item => ({
    ...item,
    color: colorForItem(item.label, item.isOther, highlightLabel),
  }));

  return {
    items,
    chartType,
    orientation: chartType === 'horizontalBar' ? 'horizontal' : 'vertical',
    highlightLabel,
    total: items.reduce((sum, item) => sum + item.value, 0),
  };
}

export function describeColumnChart(column: ChartableColumn, model: ColumnChartModel): string {
  const ranked = [...model.items]
    .filter(item => item.value > 0)
    .sort((a, b) => b.value - a.value)
    .slice(0, 3)
    .map(item => `${item.label} ${item.value}`);
  return ranked.length ? `${column.label}. Largest: ${ranked.join(', ')}.` : `${column.label}. No values.`;
}

export { getDefaultColumnChartType };
